import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const ScrollNavigator = () => {
  const [position, setPosition] = useState({ atTop: true, atBottom: false, scrollable: false });
  const frameRef = useRef(0);

  useEffect(() => {
    const update = () => {
      const root = document.scrollingElement || document.documentElement;
      const top = root.scrollTop || window.scrollY;
      const max = Math.max(0, root.scrollHeight - root.clientHeight);
      const next = { atTop: top <= 8, atBottom: max - top <= 8, scrollable: max > 16 };
      setPosition((current) => (
        current.atTop === next.atTop && current.atBottom === next.atBottom && current.scrollable === next.scrollable
          ? current : next
      ));
    };
    const scheduleUpdate = () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = 0;
        update();
      });
    };
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleUpdate) : null;
    if (observer) observer.observe(document.documentElement);
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (observer) observer.disconnect();
    };
  }, []);

  const move = (top) => window.scrollTo({ top, behavior: "smooth" });
  const moveToBottom = () => {
    const root = document.scrollingElement || document.documentElement;
    move(root.scrollHeight);
  };
  const buttonClass = "h-11 w-11 rounded-full border border-sky-300/80 bg-white/90 text-sky-800 shadow-[0_8px_24px_rgba(14,116,144,0.18),inset_0_1px_0_rgba(255,255,255,0.95)] backdrop-blur-xl transition-[opacity,transform,background-color,color] duration-200 hover:-translate-y-0.5 hover:bg-sky-600 hover:text-white disabled:opacity-35";

  return (
    <TooltipProvider delayDuration={250}>
      <div
        className={`fixed right-3 sm:right-5 z-30 flex flex-col gap-2 transition-opacity duration-200 ${position.scrollable ? "opacity-100" : "pointer-events-none opacity-0"}`}
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 5.5rem)" }}
        data-testid="scroll-navigation"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon" type="button" onClick={() => move(0)} disabled={position.atTop}
              aria-label="Scroll to top" data-testid="scroll-to-top-btn" className={buttonClass}>
              <ArrowUp className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Back to top</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon" type="button" onClick={moveToBottom}
              disabled={position.atBottom} aria-label="Scroll to bottom" data-testid="scroll-to-bottom-btn" className={buttonClass}>
              <ArrowDown className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Go to bottom</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
};